/**
 * The provider selected when `/auth/config` returns `provider: "jwt"`.
 *
 * The token lives in `sessionStorage`, not `localStorage`: a reviewer's session ending with the
 * tab is the behaviour wanted on a shared machine, and neither is proof against a script running
 * on this origin — the protection against that is the CSP and not the storage choice.
 *
 * There is no refresh token (ADR-0012). An expired token means the next request is unauthorised,
 * and the shell shows the form again rather than pretending to be signed in.
 */

import type { AuthContextValue, AuthUser } from './types';

const KEY = 'thelaw.session';

interface Stored {
  token: string;
  email: string;
  displayName: string;
  /** Epoch milliseconds. Checked locally so an obviously dead token is not sent. */
  expiresAt: number;
}

export function readSession(): Stored | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Stored;
    if (!parsed.token || typeof parsed.expiresAt !== 'number') return null;
    if (parsed.expiresAt <= Date.now()) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch {
    // Storage can be unavailable or hold something this build no longer understands. Either way
    // the answer is "not signed in", never a crash on boot.
    return null;
  }
}

export function writeSession(stored: Stored): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(stored));
  } catch {
    // A session that cannot be persisted still works for this page; nothing else depends on it.
  }
}

export function clearSession(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}

export function jwtAuth(
  session: Stored | null,
  actions: { onSignOut: () => void },
): AuthContextValue {
  const user: AuthUser | null = session
    ? {
        id: session.email,
        displayName: session.displayName,
        email: session.email,
        roles: ['admin'],
      }
    : null;

  return {
    status: session ? 'authenticated' : 'unauthenticated',
    user,
    getAccessToken: async () => session?.token ?? null,
    login: () => undefined,
    logout: () => {
      clearSession();
      actions.onSignOut();
    },
  };
}

export type { Stored as StoredSession };
